import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalyticsFilters } from '../../contexts/AnalyticsFiltersContext';
import analyticsService from '../../services/analyticsService';

import type { AnnotationItem, AnnotationsResponse, FlaggedMessageAnnotation, RadarAlert, RadarSeverity } from '../../types/radar';
import type { FlaggedMessage, AnnotationStatus } from '../TeacherAIAnalyticsPanelView/sections/InappropriateUseSection/useInappropriateUseSection';

type RadarTab = {
  id: string;
  label: string;
  count: number;
};

type SelectOption = {
  label: string;
  value: string;
};

type RadarSortOption = 'relevance' | 'date_newest' | 'date_oldest';

type RadarCategoryFilter = 'all' | 'FLAGGED_MESSAGE' | 'LEARNING_ALERT' | 'DISCOVERY';

type EmptyStateConfig = {
  iconType: string;
  iconVariant: 'success' | 'warning' | 'muted';
  title: string;
  description: string;
};

type UseRadarViewReturn = {
  activeTab: string;
  alerts: RadarAlert[];
  categoryOptions: SelectOption[];
  drawerMessage: FlaggedMessage | null;
  emptyState: EmptyStateConfig;
  error: string | null;
  groupOptions: SelectOption[];
  handleCardClick: (alert: RadarAlert) => void;
  handleCategoryChange: (option: SelectOption | null) => void;
  handleCloseDrawer: () => void;
  handleGroupsChange: (options: SelectOption[] | null) => void;
  handleMarkAllRead: () => void;
  handleSortChange: (option: SelectOption | null) => void;
  handleStatusChanged: (annotationId: string, newStatus: AnnotationStatus, reviewerName: string) => void;
  hasUnread: boolean;
  loading: boolean;
  markingAllRead: boolean;
  selectedCategory: SelectOption;
  selectedGroupOptions: SelectOption[];
  selectedSort: SelectOption;
  setActiveTab: (tab: string) => void;
  sortOptions: SelectOption[];
  t: (key: string, opts?: Record<string, unknown>) => string;
  tabs: RadarTab[];
};

function getSeverityFromScore(score: number): RadarSeverity {
  if (score > 0.8) return 'high';
  if (score > 0.5) return 'medium';
  return 'low';
}

function getSeverityFromErrorRate(errorRate: number): RadarSeverity {
  if (errorRate > 0.6) return 'high';
  if (errorRate >= 0.4) return 'medium';
  return 'low';
}

function normalizeAnnotation(item: AnnotationItem, t: (key: string, opts?: Record<string, unknown>) => string): RadarAlert {
  if (item.type === 'FLAGGED_MESSAGE') {
    const msg = item;
    const topCategory = msg.categories?.[0];
    const score = topCategory?.score ?? 0;

    return {
      id: msg.annotationId,
      type: 'inappropriate-use',
      status: msg.status,
      studentName: msg.studentName,
      studentLastName: msg.studentLastName,
      content: msg.content,
      createdAt: msg.createdAt,
      severity: getSeverityFromScore(score),
      severityScore: score,
      categoryLabel: topCategory?.category ?? t('chatbot:radar_type_inappropriate_use'),
      locations: [],
      viewed: msg.viewed,
      reviewedById: msg.annotationReviewedById,
      reviewedByName: [msg.annotationReviewedByName, msg.annotationReviewedByLastName].filter(Boolean).join(' ') || null,
      raw: msg,
    };
  }

  const alert = item;
  const locationStrings = (alert.locations ?? [])
    .map((loc) => {
      const parts = [loc.unit?.name, loc.lesson?.name].filter(Boolean);
      return parts.join(' > ');
    })
    .filter(Boolean);

  return {
    id: alert.annotationId,
    type: 'learning-block',
    status: alert.status,
    studentName: alert.studentName,
    studentLastName: alert.studentLastName,
    content: alert.kcStatement,
    createdAt: alert.createdAt,
    severity: getSeverityFromErrorRate(alert.errorRate),
    severityScore: alert.errorRate,
    categoryLabel: t('chatbot:radar_attempts', { count: alert.attemptCount }),
    locations: locationStrings,
    viewed: alert.viewed,
    reviewedById: alert.annotationReviewedById,
    reviewedByName: [alert.annotationReviewedByName, alert.annotationReviewedByLastName].filter(Boolean).join(' ') || null,
    raw: alert,
  };
}

function convertToFlaggedMessage(raw: FlaggedMessageAnnotation): FlaggedMessage {
  return {
    annotationId: raw.annotationId,
    annotationStatus: raw.status as FlaggedMessage['annotationStatus'],
    annotationReviewedAt: raw.annotationReviewedAt ?? null,
    annotationReviewedById: raw.annotationReviewedById ?? null,
    annotationNotes: raw.annotationNotes ?? null,
    messageId: raw.messageId,
    conversationId: raw.conversationId,
    content: raw.content,
    createdAt: raw.createdAt,
    studentName: raw.studentName ?? '',
    studentLastName: raw.studentLastName ?? '',
    studentExternalId: raw.studentExternalId,
    categories: raw.categories ?? [],
  };
}

function useRadarView(): UseRadarViewReturn {
  const { t } = useTranslation(['chatbot']);
  const { allCourseIds, classOptions, groupCourseMap } = useAnalyticsFilters();

  const [allAlerts, setAllAlerts] = useState<RadarAlert[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('unattended');
  const [sortBy, setSortBy] = useState<RadarSortOption>('relevance');
  const [markingAllRead, setMarkingAllRead] = useState<boolean>(false);
  const [categoryFilter, setCategoryFilter] = useState<RadarCategoryFilter>('all');
  const [selectedGroupValues, setSelectedGroupValues] = useState<string[] | null>(null);
  const [drawerMessage, setDrawerMessage] = useState<FlaggedMessage | null>(null);

  // --- Filter options ---

  const groupOptions: SelectOption[] = useMemo(
    () => classOptions.filter((opt) => opt.value !== 'all'),
    [classOptions],
  );

  const selectedGroupOptions = useMemo(() => {
    if (selectedGroupValues === null) return groupOptions;
    return groupOptions.filter((opt) => selectedGroupValues.includes(opt.value));
  }, [selectedGroupValues, groupOptions]);

  const sortOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:radar_sort_relevance'), value: 'relevance' },
      { label: t('chatbot:radar_sort_date_newest'), value: 'date_newest' },
      { label: t('chatbot:radar_sort_date_oldest'), value: 'date_oldest' },
    ],
    [t],
  );

  const selectedSort = useMemo(
    () => sortOptions.find((opt) => opt.value === sortBy) ?? sortOptions[0],
    [sortOptions, sortBy],
  );

  const categoryOptions: SelectOption[] = useMemo(
    () => [
      { label: t('chatbot:radar_filter_all_categories'), value: 'all' },
      { label: t('chatbot:radar_filter_category_learning_block'), value: 'LEARNING_ALERT' },
      { label: t('chatbot:radar_filter_category_inappropriate_use'), value: 'FLAGGED_MESSAGE' },
      { label: t('chatbot:radar_filter_category_discoveries'), value: 'DISCOVERY' },
    ],
    [t],
  );

  const selectedCategory = useMemo(
    () => categoryOptions.find((opt) => opt.value === categoryFilter) ?? categoryOptions[0],
    [categoryOptions, categoryFilter],
  );

  // --- Handlers ---

  function handleGroupsChange(options: SelectOption[] | null): void {
    if (!options || options.length === 0) {
      setSelectedGroupValues([]);
    } else if (options.length === groupOptions.length) {
      setSelectedGroupValues(null);
    } else {
      setSelectedGroupValues(options.map((opt) => opt.value));
    }
  }

  function handleSortChange(option: SelectOption | null): void {
    if (option) {
      setSortBy(option.value as RadarSortOption);
    }
  }

  function handleCategoryChange(option: SelectOption | null): void {
    if (option) {
      setCategoryFilter(option.value as RadarCategoryFilter);
    }
  }

  const handleCardClick = useCallback((alert: RadarAlert): void => {
    if (alert.type === 'inappropriate-use' && alert.raw.type === 'FLAGGED_MESSAGE') {
      setDrawerMessage(convertToFlaggedMessage(alert.raw));
    }
  }, []);

  function handleCloseDrawer(): void {
    setDrawerMessage(null);
  }

  // --- Data fetching ---

  const activeCourseIds = useMemo(() => {
    if (!selectedGroupValues || selectedGroupValues.length === 0) return allCourseIds;
    const ids = selectedGroupValues.flatMap((groupValue) => groupCourseMap[groupValue] ?? []);
    return Array.from(new Set(ids));
  }, [selectedGroupValues, allCourseIds, groupCourseMap]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const courseIds = activeCourseIds;

      if (courseIds.length === 0) {
        setAllAlerts([]);
        setLoading(false);
        return;
      }

      const data: AnnotationsResponse = await analyticsService.getAnnotations({
        courseIds,
        limit: 100,
        offset: 0,
      });

      const normalized = data.items.map((item) => normalizeAnnotation(item, t));
      setAllAlerts(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setAllAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [activeCourseIds, t]);

  useEffect(() => {
    if (allCourseIds.length > 0) {
      fetchAlerts();
    } else {
      setLoading(false);
      setAllAlerts([]);
    }
  }, [fetchAlerts, activeCourseIds.length]);

  // --- Filtering & sorting ---

  const categoryFiltered = useMemo(() => {
    if (categoryFilter === 'all') return allAlerts;
    if (categoryFilter === 'FLAGGED_MESSAGE') return allAlerts.filter((a) => a.type === 'inappropriate-use');
    if (categoryFilter === 'LEARNING_ALERT') return allAlerts.filter((a) => a.type === 'learning-block');
    // DISCOVERY: no alerts of this type yet, return empty
    return [];
  }, [allAlerts, categoryFilter]);

  const counts = useMemo(() => {
    const unattended = categoryFiltered.filter((a) => a.status === 'PENDING').length;
    const attended = categoryFiltered.filter((a) => a.status === 'REVIEWED' || a.status === 'RESOLVED').length;
    const dismissed = categoryFiltered.filter((a) => a.status === 'DISMISSED').length;
    return { unattended, attended, dismissed };
  }, [categoryFiltered]);

  const tabs: RadarTab[] = useMemo(
    () => [
      { id: 'unattended', label: t('chatbot:radar_tab_unattended'), count: counts.unattended },
      { id: 'attended', label: t('chatbot:radar_tab_attended'), count: counts.attended },
      { id: 'dismissed', label: t('chatbot:radar_tab_dismissed'), count: counts.dismissed },
    ],
    [t, counts],
  );

  const alerts = useMemo(() => {
    let filtered: RadarAlert[];
    switch (activeTab) {
      case 'unattended':
        filtered = categoryFiltered.filter((a) => a.status === 'PENDING');
        break;
      case 'attended':
        filtered = categoryFiltered.filter((a) => a.status === 'REVIEWED' || a.status === 'RESOLVED');
        break;
      case 'dismissed':
        filtered = categoryFiltered.filter((a) => a.status === 'DISMISSED');
        break;
      default:
        filtered = categoryFiltered;
    }

    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date_newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date_oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'relevance':
        default: {
          const severityOrder: Record<RadarSeverity, number> = { high: 0, medium: 1, low: 2 };
          const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
          if (severityDiff !== 0) return severityDiff;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
      }
    });
  }, [categoryFiltered, activeTab, sortBy]);

  const hasUnread = useMemo(() => alerts.some((a) => !a.viewed), [alerts]);

  const emptyState: EmptyStateConfig = useMemo(() => {
    switch (activeTab) {
      case 'attended':
        return {
          iconType: 'alert',
          iconVariant: 'warning',
          title: t('chatbot:radar_empty_attended_title'),
          description: t('chatbot:radar_empty_attended_description'),
        };
      case 'dismissed':
        return {
          iconType: 'alert',
          iconVariant: 'muted',
          title: t('chatbot:radar_empty_dismissed_title'),
          description: t('chatbot:radar_empty_dismissed_description'),
        };
      default:
        return {
          iconType: 'check',
          iconVariant: 'success',
          title: t('chatbot:radar_empty_unattended_title'),
          description: t('chatbot:radar_empty_unattended_description'),
        };
    }
  }, [activeTab, t]);

  // H4 fix: add catch for error handling
  const handleMarkAllRead = useCallback(async () => {
    const unreadIds = alerts.filter((a) => !a.viewed).map((a) => a.id);
    if (unreadIds.length === 0) return;

    setMarkingAllRead(true);
    try {
      await analyticsService.markAnnotationsAsViewed(unreadIds);
      await fetchAlerts();
    } catch {
      // Silent fail — the alerts will remain unread visually
    } finally {
      setMarkingAllRead(false);
    }
  }, [alerts, fetchAlerts]);

  const handleStatusChanged = useCallback(
    (_annotationId: string, _newStatus: AnnotationStatus, _reviewerName: string) => {
      setDrawerMessage(null);
      fetchAlerts();
    },
    [fetchAlerts],
  );

  return {
    activeTab,
    alerts,
    categoryOptions,
    emptyState,
    drawerMessage,
    error,
    groupOptions,
    handleCardClick,
    handleCategoryChange,
    handleCloseDrawer,
    handleGroupsChange,
    handleMarkAllRead,
    handleSortChange,
    handleStatusChanged,
    hasUnread,
    loading,
    markingAllRead,
    selectedCategory,
    selectedGroupOptions,
    selectedSort,
    setActiveTab,
    sortOptions,
    t,
    tabs,
  };
}

export default useRadarView;
export type { RadarCategoryFilter, RadarSortOption, RadarTab, SelectOption };
